import { Injectable } from '@angular/core';
import { delay, map, of } from 'rxjs';
import { API_CONSTANTS } from '../constants';
import {
  GeoPoint,
  MOCK_FALLBACK_CENTER,
  applyGpsJitter,
  buildMockTrailSampleTimes,
  destinationPoint,
  toLocationRow,
} from '../utils';
import { HttpClientService } from './http-client.service';

export type CreateThreatPairItem = {
  organizationId: string;
  survivorId: string;
  perpetratorId: string;
  distanceThreshold: number;
  notificationChannels: string[];
};

export interface ThreatPairZoneRadiiMeters {
  red: number;
  yellow: number;
}

/**
 * How far the mock Protected Party wanders from their real last-known position, and how long one
 * lap of that wander takes. Deliberately gentle and slow relative to the perpetrator's orbit (see
 * `PerpetratorLocationsMockInterceptor`) — the survivor isn't crossing zones, just living
 * normally, so the movement should read as "still roughly here" rather than urgent.
 */
const SURVIVOR_WANDER_RADIUS_METERS = 25;
const SURVIVOR_WANDER_PERIOD_MS = 240000;

/** A slow, small, non-circular drift around `anchor` — see `SURVIVOR_WANDER_*` above. */
function wanderPosition(anchor: GeoPoint, atMs: number): GeoPoint {
  const periodMs = SURVIVOR_WANDER_PERIOD_MS;
  const phase = (((atMs % periodMs) + periodMs) % periodMs) / periodMs;
  // abs(sin) at 2x frequency for the radius keeps it non-circular (never a perfect ring) and a
  // non-integer rotation count on the bearing keeps it from ever closing into the same loop twice.
  const distance =
    SURVIVOR_WANDER_RADIUS_METERS * (0.25 + 0.75 * Math.abs(Math.sin(Math.PI * phase * 2)));
  const bearing = (phase * 540) % 360;

  return applyGpsJitter(destinationPoint(anchor, distance, bearing), atMs, 41);
}

/**
 * Mock (NEX-1534): the Protected Party's real `last-locations` endpoint exists and works, but for
 * this demo their position is wrapped in a gentle wander so they visibly move too, rather than
 * sitting frozen wherever their last real check-in happened to be (which, on this environment's
 * seed data, can be over a month stale). `anchorPosition` — their real last-known position — is
 * still used as the geographic seed, so the demo stays anchored near their real recorded area.
 *
 * Trade-off worth knowing: every sample here is timestamped "now", so `ThreatPairLiveMapComponent`'s
 * FR-501/FR-502 stale-fix handling (hold last-known, flag low-confidence) won't visibly trigger
 * during this mock — that logic is unchanged and will engage again once this calls the real
 * endpoint directly.
 *
 * Unlike the perpetrator's feed, this one stays a plain client-side transform rather than a mocked
 * HTTP call: the real endpoint it wraps already exists and is actually being called (for the
 * anchor) — there's no future endpoint swap to rehearse here, just a demo enhancement on data
 * that's already real.
 */
function buildMockSurvivorLocationsResponse(anchorPosition: GeoPoint | null) {
  const anchor = anchorPosition ?? MOCK_FALLBACK_CENTER;
  const now = Date.now();
  const sampleTimes = buildMockTrailSampleTimes(now);

  const rows = sampleTimes.map((atMs) =>
    toLocationRow(wanderPosition(anchor, atMs), atMs, false),
  );

  return { data: { count: rows.length, rows } };
}

@Injectable({
  providedIn: 'root',
})
export class ThreatPairService {
  constructor(private http: HttpClientService) {}

  /**
   * Stands in for the not-yet-built perpetrator GPS endpoint (NEX-1534). This *is* a real HTTP
   * call — `GET /end-users/{id}/perpetrator-locations` — so the calling code is already shaped
   * exactly like it will be once a real backend exists; `PerpetratorLocationsMockInterceptor`
   * intercepts requests to this specific path and returns a synthesized response instead of
   * letting them reach the server (which doesn't have a matching route). `referencePosition`/
   * `zoneRadiiMeters` are sent as query params purely to steer the mock's synthesized orbit — the
   * interceptor reads them back off the request; a real endpoint wouldn't need or accept them,
   * since it would return the perpetrator's own device-reported GPS, not a *computed* position.
   */
  getPerpetratorLocations(
    perpetratorId: string,
    referencePosition: GeoPoint | null,
    zoneRadiiMeters: ThreatPairZoneRadiiMeters,
  ) {
    const url = `${API_CONSTANTS.END_USERS}/${perpetratorId}${API_CONSTANTS.PERPETRATOR_LOCATIONS}`;
    const params: Record<string, string | number> = {
      yellowRadius: zoneRadiiMeters.yellow,
      redRadius: zoneRadiiMeters.red,
    };
    if (referencePosition) {
      params['refLat'] = referencePosition.lat;
      params['refLng'] = referencePosition.lng;
    }
    return this.http.get(url, params);
  }

  /**
   * Mock (NEX-1534): wraps the Protected Party's real last-known position (`anchorPosition`) in a
   * gentle wander so they visibly move too — see `buildMockSurvivorLocationsResponse`'s doc
   * comment for why this one stays client-side rather than going through the interceptor.
   * `survivorId` is unused; kept so swapping this back to a direct `UserService.getLocations`
   * call is a one-line change.
   */
  getSurvivorLocations(survivorId: string, anchorPosition: GeoPoint | null) {
    void survivorId;
    return of(buildMockSurvivorLocationsResponse(anchorPosition)).pipe(delay(300));
  }

  createThreatPairs(pairs: CreateThreatPairItem[]) {
    return this.http
      .post(API_CONSTANTS.THREAT_PAIRS, pairs)
      .pipe(map((res: any) => res));
  }

  getThreatPairs(query?: any) {
    return this.http
      .get(API_CONSTANTS.THREAT_PAIRS, query)
      .pipe(map((res: any) => res));
  }

  getThreatPairAlerts(
    threatPairId: string,
    params?: {
      limit?: number;
      offset?: number;
      fromDate?: string;
      toDate?: string;
    },
  ) {
    const url = `${API_CONSTANTS.THREAT_PAIRS}/${threatPairId}/alerts`;
    return this.http.get(url, params).pipe(map((res: any) => res));
  }

  patchThreatPairStatus(id: string, body: { status: string }) {
    const url = `${API_CONSTANTS.THREAT_PAIRS}/${id}/status`;
    return this.http.patch(url, body).pipe(map((res: any) => res));
  }

  patchThreatPair(id: string, body: { survivorId: string, perpetratorIds: string[], distanceParameter: string, distanceThreshold: number, yellowCheckInTime: number, redDistanceThreshold: number, redCheckInTime: number, notificationChannels: string[] }) {
    return this.http
      .patch(`${API_CONSTANTS.THREAT_PAIRS}/${id}`, body)
      .pipe(map((res: any) => res));
  }

  deleteThreatPair(id: string) {
    return this.http
      .remove(`${API_CONSTANTS.THREAT_PAIRS}/${id}`)
      .pipe(map((res: any) => res));
  }
}
